import { msg } from '@lingui/core/macro';

import { defineEnum } from '@/shared/patterns/define-enum';

import type { ReportType } from './conciliation.wire';

// Constantes do legado (pages/WalletManagement/Conciliation/constants.ts) declaradas uma vez: rótulos em
// `msg`, valores técnicos do fio como estão.

/* eslint-disable lingui/no-unlocalized-strings -- valores técnicos do fio; os rótulos são msg */

/** Status da conciliação na lista (ConciliationDataprevList.tsx:78-92): fora do mapa, o próprio valor. */
export const CONCILIATION_STATUS = defineEnum({
  Integrating: { label: msg`Integrando`, tone: 'info', icon: 'clock' },
  WaitingManualHandling: { label: msg`Aguardando tratativa de inconsistências`, tone: 'warn', icon: 'triangle-alert' },
  Finished: { label: msg`Concluído`, tone: 'ok', icon: 'circle-check' },
  FinishedWithoutFinancial: { label: msg`Concluído sem financeiro`, tone: 'ok', icon: 'circle-check' },
  WaitingFinished: { label: msg`Aguardando conclusão`, tone: 'info', icon: 'clock' },
});

/** Etapas do processo (constants.ts:11-17). A 4 ("Concluir com financeiro") está comentada no legado. */
export const STEP = { processGenerated: 1, inconsistenciesTreatment: 2, toCompleteWithoutFinancial: 3, toComplete: 4, completed: 5 } as const;

/**
 * A régua do cabeçalho (constants.ts:82-102). O título da etapa 3 vem de um ternário sobre a **constante**
 * (`ENumStepLevels.toCompleteWithoutFinancial ? …`), sempre verdadeiro: a etapa diz sempre "Concluir sem
 * financeiro", mesmo concluída (⚠️ comportamento efetivo do legado, mantido).
 */
export const STEPS = [
  { level: STEP.processGenerated, label: msg`Processo gerado` },
  { level: STEP.inconsistenciesTreatment, label: msg`Tratativa das inconsistências` },
  { level: STEP.toCompleteWithoutFinancial, label: msg`Concluir sem financeiro` },
  { level: STEP.completed, label: msg`Concluído` },
] as const;

/** Etapa atual pelo status (ConciliationDataprevDetailsContainer.tsx:79-92). */
export function stepOf(conciliationStatus: string | null | undefined): number {
  if (conciliationStatus === 'Finished' || conciliationStatus === 'FinishedWithoutFinancial') return STEP.completed;
  if (conciliationStatus === 'WaitingFinished') return STEP.toCompleteWithoutFinancial;
  if (conciliationStatus === 'WaitingManualHandling') return STEP.inconsistenciesTreatment;
  return STEP.processGenerated;
}

/** Tipos de tratativa individual, na ordem do select (constants.ts:19-76). */
export const TREATMENT_TYPE = {
  linkInstallment: 'LinkInstallment',
  approved: 'ApprovedDisbursement',
  unapproved: 'UnapprovedDisbursement',
  canceled: 'Canceled',
} as const;

export const TREATMENT_OPTIONS = [
  { value: 'LinkInstallment', label: msg`Vincular parcelamento` },
  { value: 'ApprovedDisbursement', label: msg`Repasse averbado` },
  { value: 'UnapprovedDisbursement', label: msg`Repasse desaverbado` },
  { value: 'EmploymentTermination', label: msg`Desligamento ou término de vínculo` },
  { value: 'TransferNotRecordedByEmployer', label: msg`Repasse não escriturado pelo empregador` },
  { value: 'TemporaryMarginLoss', label: msg`Perda de margem temporária` },
  { value: 'RetainedPocketAccount', label: msg`Retido - Conta Bolsão` },
  { value: 'ReasonNotIdentified', label: msg`Motivo não identificado` },
  { value: 'IncorrectIF', label: msg`IF Incorreta` },
  { value: 'Canceled', label: msg`Cancelado` },
] as const;

/**
 * Opções do select da tratativa (ConciliationDataprevInconsistencyDrawerForm.tsx:57-77): sem parcelamento
 * vinculado sai "Repasse averbado", com ele sai "Repasse desaverbado"; o lote não vincula; "Cancelado" só
 * sem classificação nem ação sugerida (RF03).
 */
export function treatmentOptionsFor(input: { readonly hasInstallment: boolean; readonly batch: boolean; readonly hasAutomaticSuggestion: boolean }) {
  const excluded = input.hasInstallment ? TREATMENT_TYPE.unapproved : TREATMENT_TYPE.approved;
  return TREATMENT_OPTIONS.filter(
    (option) =>
      option.value !== excluded &&
      !(input.batch && option.value === TREATMENT_TYPE.linkInstallment) &&
      !(input.hasAutomaticSuggestion && option.value === TREATMENT_TYPE.canceled),
  );
}

/** Vínculo do parcelamento (constants.ts:104; conciliation.ts:303-307): "Pontual" (ou nada) é `Temporary`. */
export const LINK_TYPES = [
  { value: 'Pontual', label: msg`Pontual` },
  { value: 'Definitivo', label: msg`Definitivo` },
] as const;
export const linkTypeToWire = (linkType: string | null | undefined): 'Temporary' | 'Permanent' =>
  !linkType || linkType === 'Pontual' ? 'Temporary' : 'Permanent';

/** Rótulos das ações de tratativa via Bolsão (constants.ts:106-110). */
export const TREATMENT_ACTION_LABELS = {
  SettleFutureInstallments: msg`Liquidar parcelas futuras via Bolsão`,
  KeepInSuspenseAccount: msg`Manter no Bolsão`,
  ApplyInterestAndFine: msg`Aplicar juros e multa`,
} as const;

/** "Vazio" nos filtros de classificação, ação sugerida, CPF, contrato, fundo e corban (constants.ts:112). */
export const EMPTY_FILTER_VALUE = 'onlyEmpty';

export const CLASSIFICATION = defineEnum(
  {
    onlyEmpty: { label: msg`Vazio` },
    '1': { label: msg`à Maior` },
    '2': { label: msg`Desconto Indevido` },
    '3': { label: msg`à Menor` },
    '4': { label: msg`OK` },
  },
  { order: ['onlyEmpty', '1', '2', '3', '4'] },
);

export const SUGGESTED_ACTION = defineEnum(
  {
    onlyEmpty: { label: msg`Vazio` },
    '1': { label: msg`Liquidar parcelas futuras` },
    '2': { label: msg`Manter no Bolsão` },
    '3': { label: msg`Aplicar juros e multa` },
  },
  { order: ['onlyEmpty', '1', '2', '3'] },
);

/** Status da operação (constants.ts:134-167), sem "Encerrado": o filtro nunca oferece `Finished`. */
const STATUS_OPERATION_ENTRIES = {
  None: { label: msg`Vazio` },
  Draft: { label: msg`Rascunho` },
  ComplianceApproval: { label: msg`Aprovação de Compliance` },
  CreditApproval: { label: msg`Aprovação de Crédito` },
  Signatures: { label: msg`Coleta de Assinaturas` },
  Liquidation: { label: msg`Liquidação` },
  Disapproved: { label: msg`Reprovado` },
  Error: { label: msg`Erro` },
  Revision: { label: msg`Revisão` },
  InstrumentApproval: { label: msg`Aprovação de Instrumento` },
  Canceled: { label: msg`Cancelada` },
  WaitLiquidation: { label: msg`Aguardando Liberação para Liquidação` },
  SignaturesValidation: { label: msg`Validação de Assinaturas` },
  PaymentRevision: { label: msg`Revisão de Pagamento` },
  ManualLiquidation: { label: msg`Liquidação Manual` },
  Warranty: { label: msg`Garantia` },
  ManualWarranty: { label: msg`Garantia Manual` },
  MarginReserveApproval: { label: msg`Reserva de Margem` },
  WarrantyRevision: { label: msg`Revisão de Garantia` },
  PartnerSignaturesValidation: { label: msg`Validação de assinatura pelo parceiro` },
  OpenAccountRevision: { label: msg`Revisão de abertura de conta` },
} as const;
export const STATUS_OPERATION = defineEnum(STATUS_OPERATION_ENTRIES);

/** Aba Não encerradas: sem "Vazio" (constants.ts:169-174), que contradiz `statusOperationNot=Finished`. */
const { None: _none, ...STATUS_OPERATION_NOT_FINISHED_ENTRIES } = STATUS_OPERATION_ENTRIES;
export const STATUS_OPERATION_NOT_FINISHED = defineEnum(STATUS_OPERATION_NOT_FINISHED_ENTRIES);

export const PAYMENT_DISBURSEMENT_TYPE = defineEnum({
  DisbursementReceived: { label: msg`Repasse recebido` },
  DisbursementNotReceived: { label: msg`Repasse não recebido` },
});

/** Filtro padrão das abas Inconsistências e Não encerradas (`defaultParams`, InconsistencyContainer.tsx:263). */
export const DEFAULT_PAYMENT_DISBURSEMENT_TYPE = 'DisbursementReceived';

export const TREATMENT_FILTER = defineEnum({
  WithTreatment: { label: msg`Com tratativa` },
  WithoutTreatment: { label: msg`Sem tratativa` },
});

export const CONTRACT_STATUS = defineEnum({
  Active: { label: msg`Ativo` },
  Inactive: { label: msg`Inativo` },
  NotRecorded: { label: msg`Não escriturado` },
});

export const TREATMENT_TYPE_FILTER = defineEnum({
  ApprovedDisbursement: { label: msg`Repasse averbado` },
  UnapprovedDisbursement: { label: msg`Repasse desaverbado` },
  LinkInstallment: { label: msg`Parcelamento vinculado` },
  EmploymentTermination: { label: msg`Desligamento ou término de vínculo` },
  TransferNotRecordedByEmployer: { label: msg`Repasse não escriturado pelo empregador` },
  TemporaryMarginLoss: { label: msg`Perda de margem temporária` },
  ReasonNotIdentified: { label: msg`Motivo não identificado` },
  RetainedPocketAccount: { label: msg`Retido - Conta Bolsão` },
  IncorrectIF: { label: msg`IF Incorreta` },
  SettledFromSuspenseAccount: { label: msg`Liquidado via Bolsão` },
  SettledMultipleInstallments: { label: msg`Liquidação de múltiplas parcelas` },
  SettledInstallmentPlan: { label: msg`Parcelamento quitado` },
  Canceled: { label: msg`Cancelado` },
});

/** Status das linhas no fio (constants.ts:5-9). */
export const LINE_STATUS = { inconsistency: 'Inconsistency', conciliated: 'Conciliated', delinquency: 'Delinquency' } as const;

/** Nome do arquivo quando o `Content-Disposition` não informa (reportExport.ts:3-8). */
export const REPORT_FILE_NAMES: Readonly<Record<ReportType, string>> = {
  SettledBillings: 'cobrancas-baixadas.csv',
  SuspenseAccountInflow: 'entrou-no-bolsao.csv',
  SuspenseAccountOutflow: 'saiu-do-bolsao.csv',
  SuspenseAccountStatement: 'extrato-completo-do-bolsao.csv',
};

/** "Vazio" do filtro de carteira do detalhe: isola linhas sem carteira (DetailsContainer.tsx:51-58). */
export const EMPTY_WALLET_CODE = -1;

/* eslint-enable lingui/no-unlocalized-strings */
